import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  CurrencyEuroIcon,
  DocumentPlusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { Company } from '../types';
import { canAccessEntikRecords } from '../utils/entikRecordsAccess';

interface PendingBooking {
  id: number;
  customer_name?: string;
  customer_email?: string;
  start_time?: string;
  amount_cents?: number;
  discount_cents?: number;
  status?: string;
  service_name?: string;
}

interface EntikRecordsReservasProps {
  currentCompany: Company | null;
  userEmail?: string;
  userRole?: string;
}

export const EntikRecordsReservas: React.FC<EntikRecordsReservasProps> = ({
  currentCompany,
  userEmail,
  userRole,
}) => {
  const [pending, setPending] = useState<PendingBooking[]>([]);
  const [metrics, setMetrics] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [orphanCount, setOrphanCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [pendRes, dashRes] = await Promise.all([
      apiService.getReservasPending(),
      apiService.getReservasDashboard(),
    ]);
    if (pendRes.error) {
      setError(pendRes.message || pendRes.error);
    } else {
      const data = pendRes.data as {
        pending_remote?: PendingBooking[];
        fetch_error?: string | null;
        pending_count?: number;
        stats?: { pending_count?: number; orphan_reinvoice_count?: number };
        orphan_count?: number;
      };
      const list = data?.pending_remote || [];
      setPending(list);
      const count =
        data?.pending_count ??
        data?.stats?.pending_count ??
        list.length;
      setPendingCount(count);
      setOrphanCount(Number(data?.orphan_count ?? data?.stats?.orphan_reinvoice_count ?? 0) || 0);
      if (data?.fetch_error) {
        setError(`No se pudieron cargar reservas remotas: ${data.fetch_error}`);
      } else if (count > 0 && list.length === 0) {
        setError("Hay reservas pendientes en el servidor; pulsa Sincronizar.");
      }
    }
    if (!dashRes.error && dashRes.data) {
      const dash = dashRes.data as Record<string, unknown>;
      setMetrics(dash);
      const remoteErr = dash.reservas_fetch_error as string | null | undefined;
      if (remoteErr && !pendRes.error) {
        setError(`Reservas API: ${remoteErr}`);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (canAccessEntikRecords(currentCompany, userEmail, userRole)) load();
    else setLoading(false);
  }, [currentCompany, userEmail, userRole, load]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setMessage(null);
    const res = await apiService.syncReservas();
    setSyncing(false);
    if (res.error) {
      const detail = res.message || res.error;
      setError(
        res.error === 'Error en la petición' && detail
          ? detail
          : `Error al sincronizar: ${detail}`
      );
    } else {
      const d = res.data as {
        synced?: number;
        results?: Array<{ booking_id: number; ok: boolean; error?: string }>;
      };
      const results = d?.results || [];
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.filter((r) => !r.ok).length;
      if (failCount > 0) {
        const firstErr = results.find((r) => !r.ok)?.error;
        setError(
          `Sincronización parcial: ${okCount} OK, ${failCount} con error${firstErr ? ` (${firstErr})` : ''}`
        );
      }
      const dMsg = (d as { message?: string })?.message;
      setMessage(
        dMsg || `Sincronizacion completada: ${d?.synced ?? okCount} reserva(s) procesada(s)`
      );
      load();
    }
  };

  const handleInvoice = async (id: number) => {
    const res = await apiService.createInvoiceFromReservation(id);
    if (res.error) setError(res.message || res.error);
    else {
      const d = res.data as { invoice_number?: string; duplicate?: boolean };
      setMessage(d?.duplicate ? `Ya facturada: ${d.invoice_number}` : `Factura: ${d?.invoice_number}`);
      load();
    }
  };

  if (!canAccessEntikRecords(currentCompany, userEmail, userRole)) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-gray-600">
        Sección solo para Entik Records.
      </div>
    );
  }

  const inv = (metrics?.invoices as Record<string, unknown>) || {};

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6 flex flex-wrap justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Entik Records · Reservas</h2>
          <p className="text-sm text-gray-500">Integración con reservas.entikrecords.com</p>
        </div>
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          Sincronizar
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-green-700">{message}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white shadow rounded-lg p-4 flex gap-3">
          <CurrencyEuroIcon className="h-8 w-8 text-blue-500" />
          <div>
            <p className="text-xs text-gray-500">Ingresos reservas</p>
            <p className="text-lg font-semibold">{String(inv.reservation_revenue ?? '—')} €</p>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 flex gap-3">
          <DocumentPlusIcon className="h-8 w-8 text-amber-500" />
          <div>
            <p className="text-xs text-gray-500">Pendientes</p>
            <p className="text-lg font-semibold">{String(pendingCount ?? metrics?.reservas_pending ?? pending.length)}</p>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 flex gap-3">
          <CalendarDaysIcon className="h-8 w-8 text-green-500" />
          <div>
            <p className="text-xs text-gray-500">Facturas reservas</p>
            <p className="text-lg font-semibold">{String(inv.invoiced_count ?? '—')}</p>
          </div>
        </div>
      </div>
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b font-medium">Pendientes de facturar</div>
        {loading ? (
          <p className="p-6 text-gray-500">Cargando…</p>
        ) : pending.length === 0 ? (
          <div className="p-6 text-gray-500 space-y-2">
            <p>
              Sin pendientes en lista.
              {pendingCount > 0
                ? ` (${pendingCount} segun servidor — pulsa Sincronizar).`
                : " Usa Sincronizar para comprobar nuevas reservas pagadas."}
            </p>
            {orphanCount > 0 && (
              <p className="text-amber-700 text-sm">
                {orphanCount} reserva(s) sin factura en FactuApp; Sincronizar las recuperara.
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y">
            {pending.map((b) => {
              const gross = Math.max(0, (b.amount_cents || 0) - (b.discount_cents || 0));
              return (
                <li key={b.id} className="px-4 py-3 flex justify-between gap-2 flex-wrap">
                  <div>
                    <p className="font-medium">#{b.id} {b.customer_name}</p>
                    <p className="text-xs text-gray-500">
                      {b.service_name} · {(gross / 100).toFixed(2)} €
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInvoice(b.id)}
                    className="text-sm px-3 py-1.5 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
                  >
                    Crear factura
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EntikRecordsReservas;
